/* Light/dark theme for the app.
 *
 * Theming works by swapping CSS custom properties, not by re-styling
 * components: every surface paints `var(--page)` / `var(--surface)` /
 * `var(--text)` … and the dark values are redefined under `.tw-app-dark`
 * (see app-theme.css). Adding the `tw-app-dark` class turns the whole subtree
 * dark.
 *
 * The class is applied in TWO places (see useDarkMode + AppShell):
 *  - on the `.tw-app` shell element itself, so `.tw-app.tw-app-dark` beats the
 *    light tokens `.tw-app` declares on that same element. (Custom properties
 *    resolve by proximity: the light values on `.tw-app` would otherwise shadow
 *    any dark values merely inherited from an ancestor, so the class MUST land
 *    on the shell element, not only on an ancestor.)
 *  - on `document.documentElement`, so the page background / scrollbars and the
 *    pre-mount boot/loading splash (which renders before AppShell) go dark too,
 *    via the `html.tw-app-dark` selector.
 *
 * The always-dark surfaces (Add-Agent / Reconnect drawers) carry `.tw-app-dark`
 * on their own element unconditionally, so they stay dark in both themes
 * regardless of this toggle.
 *
 * DEFAULT = the OS theme, live: with no explicit choice the app follows
 * `prefers-color-scheme`, including changes while the app is open. Only an
 * explicit toggle persists to localStorage and pins the theme from then on.
 */

import { useEffect, useState } from "react";

// v3 (OPSS-379): one key shared with the sibling Harness Optimizer app — both
// are served from this origin (hopt at /harness), so a pin made in either app
// themes both. The old per-app key is read as a fallback so existing pins
// survive; the next explicit toggle adopts the shared key.
// (v2 note, kept for history: the v1 key was auto-written on every visit, so
// under the follow-the-OS default it read as an explicit choice — the bump
// reset everyone to OS-following.)
const KEY = "ai21.theme";
const LEGACY_KEY = "ai21-gateway.theme";
const DARK_CLASS = "tw-app-dark";

/** The explicitly chosen theme, or null when the app should follow the OS. */
function getSavedTheme(): "dark" | "light" | null {
  try {
    const saved =
      window.localStorage.getItem(KEY) ??
      window.localStorage.getItem(LEGACY_KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch {
    // Storage unavailable (private mode) — treat as no explicit choice.
  }
  return null;
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

/** Resolve the initial mode: the saved explicit choice wins; else the OS. */
export function getInitialDark(): boolean {
  const saved = getSavedTheme();
  if (saved !== null) return saved === "dark";

  return systemPrefersDark();
}

/** Reflect the mode onto the document root (for the body + pre-mount splash).
 *  Does NOT persist — only an explicit toggle does (persistTheme), so the
 *  OS-following default isn't frozen by the first visit. The `.tw-app` shell
 *  element is themed by useDarkMode via the returned class name — see the
 *  module header for why both are needed. */
export function applyTheme(dark: boolean): void {
  document.documentElement.classList.toggle(DARK_CLASS, dark);
}

/** Pin the theme as an explicit user choice (the top-bar toggle). */
function persistTheme(dark: boolean): void {
  try {
    window.localStorage.setItem(KEY, dark ? "dark" : "light");
  } catch {
    // Best effort only.
  }
}

// Apply the initial theme to the root as soon as this module loads, so the
// boot/loading splash (which renders before AppShell mounts) already shows the
// right theme and there is no light flash before the toggle's effect runs.
applyTheme(getInitialDark());

// Keep the document root following live OS changes even on screens that never
// mount useDarkMode (auth / onboarding render outside AppShell). An explicit
// saved choice always wins.
window
  .matchMedia?.("(prefers-color-scheme: dark)")
  ?.addEventListener?.("change", (e) => {
    if (getSavedTheme() === null) applyTheme(e.matches);
  });

/**
 * Owns the theme boolean, reflecting it onto the document root + persisting on
 * change. Returns the class the caller must put on the `.tw-app` shell element
 * (`tw-app-dark` when dark, else "") so the shell's own tokens flip.
 */
export interface UseDarkMode {
  /** True when dark mode is active. */
  dark: boolean;
  /** Set the theme (pins it as an explicit choice) — the profile menu's
   *  Dark/Light segmented control. */
  set: (dark: boolean) => void;
  /** Class to apply to the `.tw-app` shell element (empty in light mode). */
  shellClass: string;
}

export function useDarkMode(): UseDarkMode {
  const [dark, setDark] = useState(getInitialDark);

  useEffect(() => {
    applyTheme(dark);
  }, [dark]);

  // Follow live OS theme changes while the user hasn't chosen explicitly.
  useEffect(() => {
    if (getSavedTheme() !== null) return;

    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;

    const onChange = (e: MediaQueryListEvent) => {
      // Re-check: an explicit toggle after mount must win over the OS.
      if (getSavedTheme() === null) setDark(e.matches);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return {
    dark,
    set: (next: boolean) => {
      persistTheme(next);
      setDark(next);
    },
    shellClass: dark ? DARK_CLASS : "",
  };
}
